/* eslint-disable no-param-reassign */
import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { XMLBuilder, XMLParser } from 'fast-xml-parser';
import { Comment, Discussion } from 'api/document/types/document';

export const WORKSPACE_ID = 'rl__workspace';
export const ANNOTATION_COLOR_LS_KEY = 'annotation-color';
export const FLOATING_FORM_SPACING = 48;
export const ANNOTATION_EDIT_MODE = 'AnnotationEdit';
export const ANNOTATION_STICKY_MODE = 'AnnotationCreateSticky';
export const ANNOTATION_ARROW_MODE = 'AnnotationCreateArrow2';
export const ANNOTATION_RECT_MODE = 'AnnotationCreateRectangle2';
export const ANNOTATION_SQUIGGLY_MODE = 'AnnotationCreateFreeHand2';
export const COLORS50 = [
  '#fa4d56',
  '#ffbf00',
  '#ee5396',
  '#a56eff',
  '#4589ff',
  '#1192e8',
  '#009d9a',
  '#24a148',
];
export const ZOOM_THRESHOLD = 2.5;
export const SVG_BLOB_TYPE = "image/svg+xml;charset='UTF-8'";
export const XFDF_PREFIX =
  '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">';
export const XFDF_SUFFIX = '</xfdf>';
export const localStorageWorkspacePath = 'workspace_path';

export const parser = new XMLParser({ ignoreAttributes: false });
export const builder = new XMLBuilder({ ignoreAttributes: false });

export const fromXfdf = (comment: Comment): string =>
  comment.commentMeta && comment.commentMeta?.annotations?.xfdf
    ? builder.build(comment.commentMeta.annotations.xfdf)
    : '';

export const toXfdf = (
  instance: WebViewerInstance,
  annotation: Core.Annotations.Annotation,
): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    instance.Core.annotationManager
      .exportAnnotations({
        annotList: [annotation],
      })
      .then(resolve)
      .catch(reject);
  });
};

export const mergeAllComments = (discussions: Discussion[]): string =>
  XFDF_PREFIX +
  discussions
    .map((e) => e.comments)
    .flatMap((e) => e)
    .reduce((sum, comment) => sum + fromXfdf(comment), '') +
  XFDF_SUFFIX;

export const increaseAnnotationOpacity = (
  annotation: Core.Annotations.Annotation,
): void => {
  (annotation as Core.Annotations.CustomAnnotation).Opacity = 1;
};

export const decreaseAnnotationOpacity = (
  annotation: Core.Annotations.Annotation,
): void => {
  (annotation as Core.Annotations.CustomAnnotation).Opacity = 0.5;
};

export const disableRotation = (
  annotation: Core.Annotations.Annotation,
): void => {
  annotation.setRotationControlEnabled(false);
};

export const getRelativeYForAnnotation = (
  ann: Core.Annotations.Annotation,
  zoom: number,
): number => ann.getY() - (zoom > 1 ? 25 / zoom : 13 / zoom);

export const getRelativeXForAnnotation = (
  ann: Core.Annotations.Annotation,
  zoom: number,
): number => ann.getX() - (zoom > 1 ? 12 / zoom : 5 / zoom);

export const getAvailableToolFromWebviewerInstance = (
  instance: WebViewerInstance,
): (string | Core.Tools.ToolNames)[] => {
  const {
    ARROW,
    ARROW2,
    ARROW3,
    ARROW4,
    RECTANGLE2,
    FREEHAND2,
    HIGHLIGHT,
    HIGHLIGHT2,
    HIGHLIGHT3,
    HIGHLIGHT4,
    SQUIGGLY,
    SQUIGGLY2,
    SQUIGGLY3,
    SQUIGGLY4,
    STRIKEOUT,
    STRIKEOUT2,
    STRIKEOUT3,
    STRIKEOUT4,
    UNDERLINE,
    UNDERLINE2,
    UNDERLINE3,
    UNDERLINE4,
    STICKY,
    STICKY2,
    STICKY3,
    STICKY4,
  } = instance.Core.Tools.ToolNames;

  return [
    ARROW,
    ARROW2,
    ARROW4,
    ARROW3,
    RECTANGLE2,
    FREEHAND2,
    HIGHLIGHT,
    HIGHLIGHT2,
    HIGHLIGHT3,
    HIGHLIGHT4,
    SQUIGGLY,
    SQUIGGLY2,
    SQUIGGLY3,
    SQUIGGLY4,
    STRIKEOUT,
    STRIKEOUT2,
    STRIKEOUT3,
    STRIKEOUT4,
    UNDERLINE,
    UNDERLINE2,
    UNDERLINE3,
    UNDERLINE4,
    STICKY,
    STICKY2,
    STICKY3,
    STICKY4,
    ANNOTATION_STICKY_MODE,
  ];
};

export const fileFormatSupported = [
  'png',
  'jpg',
  'jpeg',
  'tiff',
  'tif',
  'jfif',
  'pdf',
];

export const fileFormatNotAbleToPreview = ['tiff', 'tif', 'jfif'];

export const nonImageFileToPreview = ['pdf'];
